import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GlowingOrb from "../components/ui/GlowingOrb";
import DOMAIN_CONFIG from "../config/domains";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseFunctions/config";

const SubscriptionProtectedRoute = ({ children }) => {
  const {
    user,
    userType,
    subscriptionLoading,
    loading,
    initialAuthCheckDone,
    crossDomainAuthPending
  } = useAuth();
  useLocation(); // Keep for potential future use

  // Direct Firestore check to prevent redirect loops
  const [firestoreSubscriptionChecked, setFirestoreSubscriptionChecked] = useState(false);
  const [, setFirestoreHasSubscription] = useState(false);
  const [, setFirestoreCheckError] = useState(false);
  const [lastCheckedUserId, setLastCheckedUserId] = useState(null);

  useEffect(() => {
    const checkFirestoreSubscription = async () => {
      // If no user, mark as checked immediately
      if (!user?.uid) {
        setFirestoreSubscriptionChecked(true);
        setFirestoreHasSubscription(false);
        setFirestoreCheckError(false);
        setLastCheckedUserId(null);
        return;
      }

      // If we already checked for this user, skip
      if (lastCheckedUserId === user.uid) return;

      // Reset state for new user check
      setFirestoreSubscriptionChecked(false);
      setFirestoreHasSubscription(false);
      setFirestoreCheckError(false);

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          console.log('[SubscriptionProtectedRoute] Firestore userData.hasActiveSubscription:', userData.hasActiveSubscription, 'type:', typeof userData.hasActiveSubscription);
          console.log('[SubscriptionProtectedRoute] Full userData keys:', Object.keys(userData));
          console.log('[SubscriptionProtectedRoute] subscription object:', userData.subscription);
          // Use truthy check to handle both boolean true and string "true"
          if (userData.hasActiveSubscription === true || userData.hasActiveSubscription === 'true') {
            console.log('[SubscriptionProtectedRoute] Setting firestoreHasSubscription to TRUE');
            setFirestoreHasSubscription(true);
          } else {
            console.log('[SubscriptionProtectedRoute] hasActiveSubscription is NOT true, value:', userData.hasActiveSubscription);
          }
        } else {
          console.log('[SubscriptionProtectedRoute] User document does not exist');
        }
        setFirestoreCheckError(false);
      } catch (error) {
        console.error("[SubscriptionProtectedRoute] Error checking Firestore subscription:", error);
        // On error, don't redirect - trust AuthContext value instead
        setFirestoreCheckError(true);
      }

      setLastCheckedUserId(user.uid);
      setFirestoreSubscriptionChecked(true);
    };

    // Only run after initial auth check is done
    if (initialAuthCheckDone) {
      checkFirestoreSubscription();
    }
  }, [user?.uid, initialAuthCheckDone, lastCheckedUserId]);

  // Wait for Firebase to fully restore auth state AND cross-domain auth AND subscription check to complete
  if (loading || !initialAuthCheckDone || crossDomainAuthPending || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <GlowingOrb size="md" />
      </div>
    );
  }

  if (!user) {
    // Use cross-domain redirect to auth domain
    DOMAIN_CONFIG.redirectToAuth('/login');
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <GlowingOrb size="md" />
      </div>
    );
  }

  // Wait for Firestore subscription check to complete (only after we confirm user exists)
  if (!firestoreSubscriptionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <GlowingOrb size="md" />
      </div>
    );
  }

  // Wait for userType to be loaded before making routing decisions
  if (userType === null || userType === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <GlowingOrb size="md" />
      </div>
    );
  }

  // All authenticated users have access — freemium model, no paywall
  return children;
};

export default SubscriptionProtectedRoute;
